import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import DatePicker, { registerLocale } from 'react-datepicker';
import sv from 'date-fns/locale/sv';
import CustomDropdown, { DropdownOption } from './custom-dropdown.component';
import { SearchInput } from './search/search-input.components';

/* Register the Swedish locale with react-datepicker once at module
   load — after this, passing `locale="sv"` on the <DatePicker>
   swaps month names, weekday headers, and screen-reader strings
   to Swedish. Same idiom used in employment-details and
   InvoiceByTimesheetModal. */
registerLocale('sv', sv);

/* Portal the calendar popper out of the filters bar so the
   `.date-range-thread` pill's `overflow: hidden` (rounded corners)
   never clips the calendar. Paired with `popperProps.strategy:
   'fixed'` to position relative to the viewport. */
const PopperPortal: React.FC<{ children: React.ReactNode }> = ({ children }) =>
    ReactDOM.createPortal(children, document.body);

/* String <-> Date bridges. The parent contract stays string-only
   (yyyy-mm-dd), so onStartDateChange / onEndDateChange and every
   consumer above (Redux, API filters, URL params) is unchanged —
   we convert ONLY at the picker boundary. */
const parseIso = (s?: string): Date | null => {
    if (!s) return null;
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
};
const toIso = (d: Date | null): string => {
    if (!d) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

interface DeductionFiltersProps {
    selectedYear: string;
    selectedMonth: string;
    selectedClient: string;
    searchQuery: string;
    startDate?: string;
    endDate?: string;
    onYearChange: (value: string) => void;
    onMonthChange: (value: string) => void;
    onClientChange: (value: string) => void;
    onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onStartDateChange?: (date: string) => void;
    onEndDateChange?: (date: string) => void;
    clients?: DropdownOption[];
    loading?: boolean;
}

const DeductionFilters: React.FC<DeductionFiltersProps> = ({
    selectedYear,
    selectedMonth,
    selectedClient,
    searchQuery,
    startDate = '',
    endDate = '',
    onYearChange,
    onMonthChange,
    onClientChange,
    onSearchChange,
    onStartDateChange,
    onEndDateChange,
    clients = [],
    loading = false
}) => {
    const { t, i18n } = useTranslation();
    /* Re-evaluates whenever i18next switches language; the picker
       re-renders with Swedish month/weekday labels in sv-se and
       falls back to library default (English) otherwise. */
    const calendarLocale = (i18n.language || '').toLowerCase().startsWith('sv') ? 'sv' : undefined;
    const [dateError, setDateError] = useState<string>('');

    // Re-validate when dates change (e.g. parent reset)
    useEffect(() => {
        if (startDate && endDate && startDate > endDate) {
            setDateError(t('START_DATE_AFTER_END_DATE') || 'Start date cannot be after end date');
        } else {
            setDateError('');
        }
    }, [startDate, endDate]);

    const handleStartDateChange = (date: string) => {
        if (date && endDate && date > endDate) {
            setDateError(t('START_DATE_AFTER_END_DATE') || 'Start date cannot be after end date');
        } else {
            setDateError('');
        }
        onStartDateChange?.(date);
    };

    const handleEndDateChange = (date: string) => {
        if (date && startDate && date < startDate) {
            setDateError(t('END_DATE_BEFORE_START_DATE') || 'End date cannot be before start date');
        } else {
            setDateError('');
        }
        onEndDateChange?.(date);
    };

    // Generate year options (from 1980 to current year)
    const currentYear = new Date().getFullYear();
    const yearCount = currentYear - 1980 + 1;
    const yearOptions: DropdownOption[] = [
        { value: 'all', label: t('ALL_YEARS') || 'All Years' },
        ...Array.from({ length: yearCount }, (_, i) => ({
            value: String(currentYear - i),
            label: String(currentYear - i)
        }))
    ];
        const invoicingOptions: DropdownOption[] = [
        { value: 'invoiced', label: t('INVOICED_LABEL')},
        { value: 'non-invoiced', label: t('NON_INVOICED_LABEL')},

    ];

    // Month options
    const monthOptions: DropdownOption[] = [
        { value: 'all', label: t('ALL_MONTHS') || 'All Months' },
        { value: '1', label: t('JANUARY') || 'January' },
        { value: '2', label: t('FEBRUARY') || 'February' },
        { value: '3', label: t('MARCH') || 'March' },
        { value: '4', label: t('APRIL') || 'April' },
        { value: '5', label: t('MAY') || 'May' },
        { value: '6', label: t('JUNE') || 'June' },
        { value: '7', label: t('JULY') || 'July' },
        { value: '8', label: t('AUGUST') || 'August' },
        { value: '9', label: t('SEPTEMBER') || 'September' },
        { value: '10', label: t('OCTOBER') || 'October' },
        { value: '11', label: t('NOVEMBER') || 'November' },
        { value: '12', label: t('DECEMBER') || 'December' }
    ];

    // Client options from API with "All Customers" option
    const clientOptions: DropdownOption[] = [
        { value: 'all', label: t('ALL_CUSTOMERS') || 'All Customers' },
        ...clients
    ];

    return (
        <div className="deduction-filters">
            <div className="deduction-filters__row">
                     <div className="deduction-filters__search">
                    <SearchInput
                        value={searchQuery}
                        onChange={onSearchChange}
                        placeholder={t('SEARCH_PLACEHOLDER') || 'Search here...'}
                        className="deduction-search"
                    />
                </div>
                
                {/* The Invoiced / Not Invoiced switcher used to live here as
                    a CustomDropdown. It has been lifted up to the page (above
                    the filter row) and replaced with the shared PillTabs.
                    The component still receives `selectedYear` / `onYearChange`
                    props for backwards-compat in case any caller relies on
                    them — they're simply not consumed any more in this layout. */}
                <div className="deduction-filters__dropdown">
                    <CustomDropdown
                        options={clientOptions}
                        value={selectedClient}
                        onSelect={onClientChange}
                        placeholder={t('SELECT_CUSTOMER') || 'Select Customer'}
                        className="deduction-client-dropdown"
                        menuClassName="deduction-cust-menu"
                        optionClassName="deduction-cust-option"
                        disabled={loading}
                    />
                </div>


                {/* Date Range In One Thread */}
                {onStartDateChange && onEndDateChange && (
                    <div className="deduction-filters__date-input">
                        <div className="date-range-thread">
                            <div className="date-inline-field">
                                <DatePicker
                                    selected={parseIso(startDate)}
                                    onChange={(d: Date | null) => handleStartDateChange(toIso(d))}
                                    dateFormat="yyyy/MM/dd"
                                    placeholderText="yyyy/mm/dd"
                                    className={`date-input-field date-input-field--range date-input-field--start${dateError ? ' date-input-field--error' : ''}`}
                                    wrapperClassName="deduction-filters__date-input-dp"
                                    popperContainer={PopperPortal}
                                    popperProps={{ strategy: 'fixed' }}
                                    popperClassName="deduction-filters__date-dp-popper"
                                    calendarClassName="deduction-filters__date-dp-calendar"
                                    onChangeRaw={(e: React.SyntheticEvent | undefined) => e?.preventDefault()}
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="scroll"
                                    locale={calendarLocale}
                                    autoComplete="off"
                                    disabled={loading}
                                />
                                <span className="date-inline-field__icon" aria-hidden="true">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                        <path d="M8 2V5" stroke="#68727d" strokeWidth="1.5" strokeLinecap="round" />
                                        <path d="M16 2V5" stroke="#68727d" strokeWidth="1.5" strokeLinecap="round" />
                                        <rect x="3" y="4" width="18" height="15" rx="2" stroke="#68727d" strokeWidth="1.5" />
                                        <path d="M3 10H21" stroke="#68727d" strokeWidth="1.5" />
                                    </svg>
                                </span>
                            </div>
                            <span className="date-range-thread__separator">-</span>
                            <div className="date-inline-field">
                                <DatePicker
                                    selected={parseIso(endDate)}
                                    onChange={(d: Date | null) => handleEndDateChange(toIso(d))}
                                    dateFormat="yyyy/MM/dd"
                                    placeholderText="yyyy/mm/dd"
                                    className={`date-input-field date-input-field--range date-input-field--end${dateError ? ' date-input-field--error' : ''}`}
                                    wrapperClassName="deduction-filters__date-input-dp"
                                    popperContainer={PopperPortal}
                                    popperProps={{ strategy: 'fixed' }}
                                    popperClassName="deduction-filters__date-dp-popper"
                                    calendarClassName="deduction-filters__date-dp-calendar"
                                    onChangeRaw={(e: React.SyntheticEvent | undefined) => e?.preventDefault()}
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="scroll"
                                    locale={calendarLocale}
                                    autoComplete="off"
                                    disabled={loading}
                                    minDate={parseIso(startDate) || undefined}
                                />
                                <span className="date-inline-field__icon" aria-hidden="true">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                        <path d="M8 2V5" stroke="#68727d" strokeWidth="1.5" strokeLinecap="round" />
                                        <path d="M16 2V5" stroke="#68727d" strokeWidth="1.5" strokeLinecap="round" />
                                        <rect x="3" y="4" width="18" height="15" rx="2" stroke="#68727d" strokeWidth="1.5" />
                                        <path d="M3 10H21" stroke="#68727d" strokeWidth="1.5" />
                                    </svg>
                                </span>
                            </div>
                        </div>
                        {dateError && (
                            <span className="date-range-thread__error" role="alert">
                                {dateError}
                            </span>
                        )}
                    </div>
                )}

                {/* <div className="deduction-filters__dropdown">
                    <CustomDropdown
                        options={yearOptions}
                        value={selectedYear}
                        onSelect={onYearChange}
                        placeholder={t('SELECT_YEAR') || 'Select Year'}
                        className="deduction-year-dropdown"
                    />
                </div>
                
                <div className="deduction-filters__dropdown">
                    <CustomDropdown
                        options={monthOptions}
                        value={selectedMonth}
                        onSelect={onMonthChange}
                        placeholder={t('SELECT_MONTH') || 'Select Month'}
                        className="deduction-month-dropdown"
                    />
                </div> */}


           
            </div>
        </div>
    );
};

export default DeductionFilters;
